/**
 * Complexity Indicator — shared types and configuration.
 */

import type { Id } from "@cvx/_generated/dataModel";
import {
  Building2,
  Landmark,
  Users,
  UserRound,
  type LucideIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IndicatorViewProps {
  taskId: Id<"summarizationTasks">;
  jobId: Id<"jobs">;
  summarizationRunId: Id<"summarizationRuns">;
  onBack: () => void;
  onRerun: () => void;
  isViewer?: boolean;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export interface CriterionDetail {
  met: boolean;
  detail: string;
  evidenceValue?: number | null;
  confidence: "high" | "medium" | "low";
}

export interface FindingWithDocs {
  criterionKey: string;
  finding: string;
  met: boolean;
  evidenceValue?: number;
  confidence: string;
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
}

export interface IndicatorOutput {
  signal?: "green" | "amber" | "red";
  criteriaMet?: number;
  criteria?: Record<string, CriterionDetail>;
  securedInterestsFlag?: { flagged: boolean; detail: string };
  recommendation?: string;
  documentsAnalysed?: number;
}

// ---------------------------------------------------------------------------
// Criterion config
// ---------------------------------------------------------------------------

export interface CriterionConfig {
  key: "companyAssets" | "creditorThreshold" | "directorAssets";
  dbKey: string;
  label: string;
  description: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}

const companyAssetsCriterion: CriterionConfig = {
  key: "companyAssets",
  dbKey: "company_assets",
  label: "Company Assets",
  description: "Does the company have real assets of material value?",
  icon: Building2,
  iconBg: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  iconColor: "text-blue-600 dark:text-blue-400",
};

const creditorThresholdCriterion: CriterionConfig = {
  key: "creditorThreshold",
  dbKey: "creditor_threshold",
  label: "Creditor Threshold",
  description: "Are creditors owed $1,000,000 or more in total?",
  icon: Landmark,
  iconBg:
    "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  iconColor: "text-amber-600 dark:text-amber-400",
};

const directorAssetsCriterion: CriterionConfig = {
  key: "directorAssets",
  dbKey: "director_assets",
  label: "Director Personal Assets",
  description: "Do director(s) have identifiable personal assets?",
  icon: Users,
  iconBg:
    "bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800",
  iconColor: "text-violet-600 dark:text-violet-400",
};

/** Bankrupt's Assets reuses the company_assets DB key, reframed for an individual. */
const bankruptAssetsCriterion: CriterionConfig = {
  key: "companyAssets",
  dbKey: "company_assets",
  label: "Bankrupt's Assets",
  description: "Does the bankrupt have personal assets of material value?",
  icon: UserRound,
  iconBg: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  iconColor: "text-blue-600 dark:text-blue-400",
};

/** Corporate matters: company assets, creditor threshold, director personal assets. */
export const CORPORATE_CRITERIA: CriterionConfig[] = [
  companyAssetsCriterion,
  creditorThresholdCriterion,
  directorAssetsCriterion,
];

/** Personal bankruptcy: bankrupt's assets + creditor threshold (no directors). */
export const PERSONAL_CRITERIA: CriterionConfig[] = [
  bankruptAssetsCriterion,
  creditorThresholdCriterion,
];

/** Selects the criteria list for the job's case type. */
export function criteriaForCaseType(isPersonal: boolean): CriterionConfig[] {
  return isPersonal ? PERSONAL_CRITERIA : CORPORATE_CRITERIA;
}
